import { useCallback, useMemo } from 'react';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { BannerNotification, Text } from '@frontend/design-system';
import { LocationAccordionInfo, LocationAccordionList } from '../../components';
import { WaitlistAppointmentDetailsStep } from '../../components/waitlist-appointment-details-step';
import { useSetLocationsWaitlistTotalCount } from '../../hooks';
import { useWaitlistShallowStore } from '../../stores';
import { SpinnerLoader } from '../tray-content/components/spinning-loader';
import { useWaitlistTrayContext, WaitlistTrayContext, WaitlistTrayContextType } from './context';
import { CountInfo } from './count-info';
import { TrayContent } from './tray-content';

export const WaitListTrayContent = () => {
  const { t } = useTranslation('schedule-quick-fill');
  const { selectedLocationIds } = useAppScopeStore();
  const { locationIdWiseQuickFillFeature } = QuickFillHooks.useQuickFillFeatureDetails();
  const { currentStep } = useWaitlistShallowStore('currentStep');
  const { isWaitlistDirectBookingEnabled } = QuickFillHooks.useGetWaitlistFeatureFlagDetails();

  const validLocationIds = useMemo(
    () => selectedLocationIds.filter((id) => locationIdWiseQuickFillFeature?.[id] === true),
    [locationIdWiseQuickFillFeature, selectedLocationIds]
  );

  const hasSingleLocationSelected = selectedLocationIds.length === 1;

  const subtitle = isWaitlistDirectBookingEnabled
    ? t('Send waitlisted customers a direct booking link to fill last-minute cancellations fast')
    : t(
        'Easily fill last-minute cancellations or openings in your schedule by messaging your customers who need to get in for an appointment as soon as possible.'
      );
  return (
    <>
      <Text size='medium' color='light' css={{ padding: theme.spacing(0, 6, 2, 3) }}>
        {subtitle}
      </Text>
      <div
        css={{
          height: '100%',
          overflow: 'hidden',
          backgroundColor: currentStep === 'select-persons' ? theme.colors.neutral5 : theme.colors.white,
          padding: !hasSingleLocationSelected || currentStep === 'compose-message' ? theme.spacing(3, 3, 0, 3) : 0,
          borderTop: currentStep === 'select-persons' ? 'none' : `1px solid ${theme.colors.neutral10}`,
        }}
      >
        {validLocationIds.length === 0 ? (
          <BannerNotification
            css={{ margin: theme.spacing(3) }}
            fullWidth
            status='info'
            message={t('Waitlist is not enabled for any of your selected locations.')}
          />
        ) : currentStep === 'select-persons' ? (
          <WaitListLocationAccordionList locationIds={validLocationIds} />
        ) : (
          <WaitlistAppointmentDetailsStep />
        )}
      </div>
    </>
  );
};

const WaitListLocationAccordionList = ({ locationIds }: { locationIds: string[] }) => {
  const { getScopeName } = useAppScopeStore();
  const { isLoading } = useSetLocationsWaitlistTotalCount(locationIds);
  const { selectedLocationId: selectedLocationIdFromContext } = useWaitlistTrayContext();
  const {
    locationWiseTotalCounts,
    selectedLocationId: selectedLocationIdFromStore,
    selectedPersonIds,
  } = useWaitlistShallowStore('locationWiseTotalCounts', 'selectedLocationId', 'selectedPersonIds');

  const selectedLocationId = selectedLocationIdFromContext || selectedLocationIdFromStore;
  const selectedPersonCount = selectedPersonIds?.length || 0;
  const validLocationInfoList = useMemo<LocationAccordionInfo[]>(
    () =>
      locationIds.map((locationId: string) => ({
        locationId,
        name: getScopeName(locationId),
        count: (
          <CountInfo
            selectedCount={locationId === selectedLocationId ? selectedPersonCount : 0}
            totalCount={locationWiseTotalCounts[locationId] || 0}
            textColor='inherit'
          />
        ),
      })),
    [getScopeName, locationIds, locationWiseTotalCounts, selectedLocationId, selectedPersonCount]
  );
  const handleAccordionExpand = useCallback(
    (locationId: string) => {
      if (isLoading) return;
      SchedulerV3.trackPendoEvent('waitlist_location_expanded', {
        locationId,
        noOfWaitlistEntries: locationWiseTotalCounts[locationId] || 0,
      });
    },
    [isLoading, locationWiseTotalCounts]
  );

  if (isLoading) return <SpinnerLoader height={100} />;

  return (
    <LocationAccordionList
      locationInfoList={validLocationInfoList}
      initiallyExpandedLocationId={selectedLocationId}
      ContentComponent={TrayContent}
      hideTimezone
      onExpand={handleAccordionExpand}
    />
  );
};

export type { WaitlistTrayContextType };
export { WaitlistTrayContext };
